import React, { useState } from "react";
import { UserPlus, Trash2, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Btn, Input } from "../UI";
import { usd, pct, errMsg } from "./format";

const StaffRow = ({ ev, s, reload }) => {
  const [n, setN] = useState(10);
  const [busy, setBusy] = useState(false);
  const st = s.stats || {};
  const assign = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/promotions/events/${ev.id}/codes/assign`, { staff_id: s.id, count: parseInt(n) || 1 });
      toast.success(data.assigned ? `${data.assigned} code(s) handed to ${s.name}` : "No unassigned codes left — allocate more first");
      reload();
    } catch (e) { toast.error(errMsg(e, "Assign failed")); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${s.name}? Their codes stay valid but lose attribution.`)) return;
    try { await api.delete(`/promotions/events/${ev.id}/staff/${s.id}`); toast.success("Staff removed"); reload(); }
    catch (e) { toast.error(errMsg(e, "Remove failed")); }
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2 text-xs flex-wrap" data-testid={`staff-row-${s.id}`}>
      <span className="w-7 h-7 rounded-full bg-amber-400/15 text-amber-300 grid place-items-center font-bold text-[10px]">{s.initials}</span>
      <span className="text-slate-200 font-medium">{s.name}</span>
      <span className="text-slate-400">{st.live || 0} codes · {st.scanned || 0} scanned · {st.claimed || 0} claimed ({pct(st.claim_rate)}) · {st.redeemed || 0} redeemed · <b className="text-amber-300">{usd(st.redeemed_value)}</b></span>
      <span className="ml-auto flex items-center gap-1.5">
        <input type="number" min="1" value={n} onChange={(e) => setN(e.target.value)} className="w-14 bg-white/5 border border-white/10 rounded px-1.5 py-1 text-xs" data-testid={`staff-assign-count-${s.id}`} />
        <Btn variant="secondary" className="!py-1 !px-2 !text-xs" onClick={assign} disabled={busy || !ev.unassigned_codes} title="Hand unassigned codes to this person" data-testid={`staff-assign-btn-${s.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />} Hand out
        </Btn>
        <button onClick={remove} className="text-slate-500 hover:text-rose-300" title="Remove" data-testid={`staff-remove-btn-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </span>
    </div>
  );
};

export const StaffPanel = ({ ev, reload }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a name");
    try {
      await api.post(`/promotions/events/${ev.id}/staff`, { name, email: email || null });
      setName(""); setEmail(""); toast.success("Staff added"); reload();
    } catch (e) { toast.error(errMsg(e, "Could not add staff")); }
  };
  return (
    <section data-testid="staff-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Booth staff · hand-out attribution</div>
        <span className="text-[11px] text-slate-500" data-testid="unassigned-codes">{ev.unassigned_codes || 0} unassigned codes</span>
      </div>
      <div className="space-y-1.5">
        {(ev.staff || []).length === 0 && <div className="text-xs text-slate-500">No staff yet — add the people working the booth to see who hands out the cards that convert.</div>}
        {(ev.staff || []).map((s) => <StaffRow key={s.id} ev={ev} s={s} reload={reload} />)}
      </div>
      <div className="flex items-end gap-2 mt-3 flex-wrap">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="w-44" data-testid="staff-name-input" />
        <Input label="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@…" className="w-52" data-testid="staff-email-input" />
        <Btn variant="secondary" onClick={add} data-testid="staff-add-btn"><UserPlus className="w-4 h-4" /> Add staff</Btn>
      </div>
    </section>
  );
};
